import * as React from 'react';
import { ButtonProps, Stack, Typography } from '@mui/material';
import { lowerCase, map } from 'lodash';
import { useMixpanel } from 'react-mixpanel-browser';

import { VersionData } from '@/api/devices';
import { OtaCheckId, OtaCheckResult, OtaReadiness, VersionStateWithReleasedAt } from '@/api/ota/types';
import { BATTERY_SECTION_ID } from '@/components/spotter/statusTab/BatterySection';
import {
    FIRMWARE_UPDATE_ALERT_TITLE,
    NewerVersionReleaseStatement,
    USB_INSTRUCTIONS_HREF,
    UsbInstallerLink,
    useTrackUsbInstallerClick,
} from '@/components/spotter/statusTab/firmwareUpdateAlertParts';
import {
    InlineAlert,
    InlineAlertBody,
    InlineAlertPrimaryButton,
    InlineAlertSecondaryButton,
    InlineAlertTitle,
} from '@/components/ui/InlineAlert';
import { useSpotterContext } from '@/context/SpotterContext';
import { spotterIdHash } from '@/hooks/useSpotterHash';
import { contactSupportHref } from '@/utils/urlHelpers';

type OtaIneligibleButtonProps = {
    spotterId: string;
    messageId: string;
    failingChecks: OtaCheckResult[];
};

type OtaIneligibleMessage = {
    messageId: string;
    title: string;
    body: string;
    /** Append the sentence that links to the USB installer instructions. */
    offersUsbInstaller: boolean;
    /** List the detail of each failing check below the body, when any check fails. */
    showsFailingCheckDetails: boolean;
    buttons: (props: OtaIneligibleButtonProps) => JSX.Element;
};

type OtaIneligibleActionButtonProps = ButtonProps &
    OtaIneligibleButtonProps & {
        rank: 'primary' | 'secondary';
    };

const useTrackOtaIneligibleClick = ({ spotterId, messageId, failingChecks }: OtaIneligibleButtonProps) => {
    const mixpanel = useMixpanel();

    return (e: React.MouseEvent<HTMLElement>, href: string | undefined) =>
        mixpanel?.track('Click OTA ineligible alert button', {
            spotterId,
            messageId,
            failingCheckIds: map(failingChecks, 'id'),
            href,
            buttonText: e.currentTarget.textContent,
        });
};

const OtaIneligibleButton = ({
    onClick,
    rank,
    spotterId,
    messageId,
    failingChecks,
    ...props
}: OtaIneligibleActionButtonProps) => {
    const trackClick = useTrackOtaIneligibleClick({ spotterId, messageId, failingChecks });
    const ButtonComponent = rank === 'primary' ? InlineAlertPrimaryButton : InlineAlertSecondaryButton;

    return (
        <ButtonComponent
            {...props}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                trackClick(e, props.href);
                onClick?.(e);
            }}
        />
    );
};

const LearnMore = ({ rank, ...props }: OtaIneligibleButtonProps & { rank: 'primary' | 'secondary' }) => {
    const trackClick = useTrackOtaIneligibleClick(props);
    const ButtonComponent = rank === 'primary' ? InlineAlertPrimaryButton : InlineAlertSecondaryButton;

    return (
        <ButtonComponent
            href={USB_INSTRUCTIONS_HREF}
            onClick={(e: React.MouseEvent<HTMLElement>) => trackClick(e, USB_INSTRUCTIONS_HREF)}
        >
            Learn more
        </ButtonComponent>
    );
};

const describeFailingChecks = (failingChecks: OtaCheckResult[]) =>
    failingChecks.map(({ label, detail }) => (detail ? `${label}: ${detail}` : label)).join('\n');

type ContactSupportProps = OtaIneligibleButtonProps & {
    rank: 'primary' | 'secondary';
    subject: string;
    /** Open the email body. The failing checks are appended below it. */
    body: string;
};

const ContactSupport = ({ subject, body, ...props }: ContactSupportProps) => (
    <OtaIneligibleButton
        href={contactSupportHref({
            subject,
            body: `${body}\n\n${describeFailingChecks(props.failingChecks)}\n\n`,
        })}
        {...props}
    >
        Contact Support
    </OtaIneligibleButton>
);

const supportSubject = (spotterId: string) => `Spotter firmware update not available: ${spotterId}`;

const deviceSettingsHref = (spotterId: string) => `/spotter/${spotterId}#configuration`;

const CONTACT_SUPPORT_MESSAGE = {
    messageId: 'contact-support',
    title: FIRMWARE_UPDATE_ALERT_TITLE,
    body: 'Remote updates are not available for your device at this time. Please contact Support to update your system.',
    offersUsbInstaller: true,
    showsFailingCheckDetails: true,
    buttons: (props) => (
        <>
            <LearnMore rank="secondary" {...props} />
            <ContactSupport
                rank="primary"
                subject={supportSubject(props.spotterId)}
                body={`Hi Spotter support, ${props.spotterId} cannot start an over-the-air firmware update because these checks did not pass:`}
                {...props}
            />
        </>
    ),
} as const satisfies OtaIneligibleMessage;

export const OTA_INELIGIBLE_MESSAGES = [
    {
        messageId: 'enable-telemetry',
        title: FIRMWARE_UPDATE_ALERT_TITLE,
        body: "Enable this Spotter's cellular telemetry to unlock remote over-the-air updates.",
        offersUsbInstaller: true,
        showsFailingCheckDetails: false,
        buttons: (props) => (
            <>
                <LearnMore rank="secondary" {...props} />
                <OtaIneligibleButton rank="primary" href={`/telemetry#${spotterIdHash(props.spotterId)}`} {...props}>
                    Enable telemetry
                </OtaIneligibleButton>
            </>
        ),
    },
    {
        messageId: 'modem-update-needed',
        title: FIRMWARE_UPDATE_ALERT_TITLE,
        body: "Your Spotter's cellular modem needs to be updated before it can receive remote over-the-air updates. Contact Support for help updating the cellular modem.",
        offersUsbInstaller: true,
        showsFailingCheckDetails: false,
        buttons: (props) => (
            <>
                <LearnMore rank="secondary" {...props} />
                <ContactSupport
                    rank="primary"
                    subject={supportSubject(props.spotterId)}
                    body={`Hi Spotter support, ${props.spotterId} cannot start an over-the-air firmware update because its cellular modem firmware needs an update.`}
                    {...props}
                />
            </>
        ),
    },
    {
        messageId: 'disable-idle-mode',
        title: FIRMWARE_UPDATE_ALERT_TITLE,
        body: 'Switch from IDLE mode to RUN mode to unlock remote over-the-air updates.',
        offersUsbInstaller: true,
        showsFailingCheckDetails: false,
        buttons: (props) => (
            <>
                <LearnMore rank="secondary" {...props} />
                <OtaIneligibleButton rank="primary" href={deviceSettingsHref(props.spotterId)} {...props}>
                    Switch to RUN mode
                </OtaIneligibleButton>
            </>
        ),
    },
    {
        messageId: 'prerelease-firmware',
        title: FIRMWARE_UPDATE_ALERT_TITLE,
        body: "Your Spotter's current firmware version does not support remote over-the-air updates. Contact Support for help.",
        offersUsbInstaller: true,
        showsFailingCheckDetails: false,
        buttons: (props) => (
            <>
                <LearnMore rank="secondary" {...props} />
                <ContactSupport
                    rank="primary"
                    subject={supportSubject(props.spotterId)}
                    body={`Hi Spotter support, ${props.spotterId} cannot start an over-the-air firmware update because its firmware is a pre-release version.`}
                    {...props}
                />
            </>
        ),
    },
    {
        messageId: 'blocking-errors',
        title: FIRMWARE_UPDATE_ALERT_TITLE,
        body: 'Your Spotter has errors that require additional attention. Contact Support for help.',
        offersUsbInstaller: false,
        showsFailingCheckDetails: false,
        buttons: (props) => (
            <ContactSupport
                rank="primary"
                subject={supportSubject(props.spotterId)}
                body={`Hi Spotter support, ${props.spotterId} cannot start an over-the-air firmware update because it reports errors.`}
                {...props}
            />
        ),
    },
    {
        messageId: 'no-system-config',
        title: FIRMWARE_UPDATE_ALERT_TITLE,
        body: 'Your Spotter has not reported its status. Remote over-the-air updates will not be available until it does. Try charging your Spotter, turning it off and on again, and putting it outside with a clear view of the sky. Contact Support if this problem persists.',
        offersUsbInstaller: true,
        showsFailingCheckDetails: false,
        buttons: (props) => (
            <>
                <LearnMore rank="secondary" {...props} />
                <ContactSupport
                    rank="primary"
                    subject={supportSubject(props.spotterId)}
                    body={`Hi Spotter support, ${props.spotterId} cannot start an over-the-air firmware update because it has not reported its system config.`}
                    {...props}
                />
            </>
        ),
    },
    {
        messageId: 'sd-card-full',
        title: FIRMWARE_UPDATE_ALERT_TITLE,
        body: "Your Spotter's SD card does not have enough space for a firmware update. Retrieve your system and free up at least 2GB of space on the SD card.",
        offersUsbInstaller: false,
        showsFailingCheckDetails: false,
        buttons: (props) => <LearnMore rank="primary" {...props} />,
    },
    {
        messageId: 'messages-queued',
        title: FIRMWARE_UPDATE_ALERT_TITLE,
        body: 'There are already messages on their way to your Spotter. Remote over-the-air updates will not be available until they go through. Contact Support if this takes more than 24 hours.',
        offersUsbInstaller: true,
        showsFailingCheckDetails: false,
        buttons: (props) => (
            <>
                <LearnMore rank="secondary" {...props} />
                <ContactSupport
                    rank="primary"
                    subject={supportSubject(props.spotterId)}
                    body={`Hi Spotter support, ${props.spotterId} cannot start an over-the-air firmware update because cellular messages to it are still queued.`}
                    {...props}
                />
            </>
        ),
    },
    {
        messageId: 'pending-config-change',
        title: FIRMWARE_UPDATE_ALERT_TITLE,
        body: "Your Spotter has a pending configuration change. Remote over-the-air updates won't be available until it goes through. If the configuration change is taking longer than expected, you can cancel it.",
        offersUsbInstaller: false,
        showsFailingCheckDetails: false,
        buttons: (props) => (
            <>
                <OtaIneligibleButton rank="primary" href={deviceSettingsHref(props.spotterId)} {...props}>
                    View settings
                </OtaIneligibleButton>
            </>
        ),
    },
    {
        messageId: 'no-recent-cellular',
        title: FIRMWARE_UPDATE_ALERT_TITLE,
        body: "Your Spotter's cellular connection is unstable. Remote over-the-air updates won't be available until your Spotter can establish a stable cellular connection.",
        offersUsbInstaller: true,
        showsFailingCheckDetails: false,
        buttons: (props) => <LearnMore rank="primary" {...props} />,
    },
    {
        messageId: 'low-battery',
        title: FIRMWARE_UPDATE_ALERT_TITLE,
        body: "Your Spotter's battery is too low. Charge it to at least 3.7 V to unlock remote over-the-air firmware updates.",
        offersUsbInstaller: true,
        showsFailingCheckDetails: false,
        buttons: (props) => (
            <>
                <LearnMore rank="secondary" {...props} />
                <OtaIneligibleButton
                    rank="primary"
                    onClick={() =>
                        document
                            .getElementById(BATTERY_SECTION_ID)
                            ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                    {...props}
                >
                    View battery status
                </OtaIneligibleButton>
            </>
        ),
    },
    CONTACT_SUPPORT_MESSAGE,
] as const satisfies readonly OtaIneligibleMessage[];

type OtaIneligibleMessageId = (typeof OTA_INELIGIBLE_MESSAGES)[number]['messageId'];

/**
 * Rows in priority order. The first row with a failing check chooses the message, so a row placed higher wins
 * over every row below it.
 */
const OTA_INELIGIBLE_PRIORITY = [
    { checkIds: ['subscription_active'], messageId: 'enable-telemetry' },
    {
        checkIds: ['cellular_telemetry_active', 'subscription_tag_cellular_telemetry_active'],
        messageId: 'enable-telemetry',
    },
    { checkIds: ['notecard_firmware_version_ok'], messageId: 'modem-update-needed' },
    { checkIds: ['not_idle'], messageId: 'disable-idle-mode' },
    { checkIds: ['spotter_firmware_not_prerelease'], messageId: 'prerelease-firmware' },
    { checkIds: ['no_blocking_errors'], messageId: 'blocking-errors' },
    { checkIds: ['has_system_config'], messageId: 'no-system-config' },
    { checkIds: ['sd_card_space_ok'], messageId: 'sd-card-full' },
    { checkIds: ['no_queued_messages'], messageId: 'messages-queued' },
    { checkIds: ['no_pending_config_change'], messageId: 'pending-config-change' },
    { checkIds: ['communicated_over_cellular'], messageId: 'no-recent-cellular' },
    { checkIds: ['battery_ok'], messageId: 'low-battery' },
] as const satisfies readonly { checkIds: readonly OtaCheckId[]; messageId: OtaIneligibleMessageId }[];

/**
 * Return the message for the highest-priority failing check. Return `'contact-support'` when no failing check has a
 * row in {@link OTA_INELIGIBLE_PRIORITY}, which includes check IDs the backend adds before the frontend knows them.
 */
export const whichOtaIneligibleMessage = (checks: OtaCheckResult[]): OtaIneligibleMessageId => {
    const failingCheckIds = new Set(checks.filter(({ passed }) => !passed).map(({ id }) => id));
    const row = OTA_INELIGIBLE_PRIORITY.find(({ checkIds }: { checkIds: readonly OtaCheckId[] }) =>
        checkIds.some((checkId) => failingCheckIds.has(checkId))
    );
    return row?.messageId ?? CONTACT_SUPPORT_MESSAGE.messageId;
};

const findOtaIneligibleMessage = (messageId: OtaIneligibleMessageId): OtaIneligibleMessage =>
    OTA_INELIGIBLE_MESSAGES.find((message) => message.messageId === messageId) ?? CONTACT_SUPPORT_MESSAGE;

export const NotOtaEligibleAlert = ({
    latestVersion,
    eligibility,
}: {
    latestVersion?: VersionStateWithReleasedAt | VersionData;
    eligibility: NonNullable<OtaReadiness['eligibility']>;
}) => {
    const { spotterId } = useSpotterContext();
    const trackUsbInstallerClick = useTrackUsbInstallerClick();

    if (!spotterId) {
        return null;
    }

    const message = findOtaIneligibleMessage(whichOtaIneligibleMessage(eligibility.checks));
    const failingChecks = eligibility.checks.filter(({ passed }) => !passed);

    return (
        <OtaIneligibleAlertBox
            message={message}
            spotterId={spotterId}
            latestVersion={latestVersion}
            failingChecks={failingChecks}
            onUsbInstallerLinkClick={() =>
                trackUsbInstallerClick({
                    reason: 'not-eligible',
                    control: 'link',
                    messageId: message.messageId,
                    failingCheckIds: map(failingChecks, 'id'),
                })
            }
        />
    );
};

/**
 * Render one message with no data hooks, so {@link DemoOtaIneligibleAlertCatalog} can render every message outside a
 * device page.
 */
const OtaIneligibleAlertBox = ({
    message,
    spotterId,
    latestVersion,
    failingChecks,
    onUsbInstallerLinkClick,
}: {
    message: OtaIneligibleMessage;
    spotterId: string;
    latestVersion?: VersionStateWithReleasedAt | VersionData;
    failingChecks: OtaCheckResult[];
    onUsbInstallerLinkClick?: () => void;
}) => {
    const { messageId, title, body, offersUsbInstaller, showsFailingCheckDetails, buttons } = message;

    return (
        <InlineAlert severity="tip" icon={false} actionButton={buttons({ spotterId, messageId, failingChecks })}>
            <InlineAlertTitle>{title}</InlineAlertTitle>
            <InlineAlertBody testid={`ota-ineligible-${messageId}`}>
                <NewerVersionReleaseStatement version={latestVersion} /> {body}
                {offersUsbInstaller && (
                    <>
                        {' '}
                        Alternatively, follow the <UsbInstallerLink onClick={onUsbInstallerLinkClick} />.
                    </>
                )}
            </InlineAlertBody>
            {showsFailingCheckDetails && failingChecks.length > 0 && (
                <Stack data-testid={`ota-ineligible-${messageId}-failing-checks`}>
                    <Typography variant="body2">These checks did not pass:</Typography>
                    <Typography variant="body2" component="ul" sx={{ my: 0 }}>
                        {failingChecks.map(({ id, label, detail }) => (
                            <li key={id}>{detail ?? label}</li>
                        ))}
                    </Typography>
                </Stack>
            )}
        </InlineAlert>
    );
};

const DEMO_FAILING_CHECKS: OtaCheckResult[] = [
    {
        id: 'bridge_enabled',
        label: 'Bridge enabled',
        passed: false,
        detail: 'Device has bridge hardware, but the bridge is disabled. Enable the bridge before updating.',
    },
];

export const DemoOtaIneligibleAlertCatalog = () => (
    <>
        {OTA_INELIGIBLE_MESSAGES.map((message) => (
            <Stack key={message.messageId} sx={{ m: 1 }}>
                <Typography>{lowerCase(message.messageId)}:</Typography>
                <OtaIneligibleAlertBox
                    message={message}
                    spotterId="SPOT-33538C"
                    latestVersion={
                        {
                            spotterVersion: '2.16.8',
                            spotterVersionReleasedAt: '2026-09-27',
                        } as VersionStateWithReleasedAt
                    }
                    failingChecks={DEMO_FAILING_CHECKS}
                />
            </Stack>
        ))}
    </>
);
